// LegalFloorReportPage.tsx — /report-illegal, reachable without an account.
//
// The Terms and the release notes both promise a reporting route that does not
// require signing in. The route on the server existed; there was no page, so
// the promise pointed at nothing. This is the door.
//
// Everything here is shaped by who arrives at it. They may not have an account,
// may be in distress, may be a parent or a lawyer rather than the victim, and
// may be frightened of being identified. So: no login, no required contact
// details, no "are you the person in the image", and no cheerful confirmation
// copy. It says what will happen and stops.
//
// It lives under ShellLayout, not the bare canvas, so a signed-out visitor
// still gets the rail, the topbar and a way onward — someone who lands here
// from an outside link must not be stranded on a single form.

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Button, Callout, Textarea, TextField } from '@/ui';
import { Icon } from '@/icons';
import { usePageTitle } from '@/hooks';
import { ApiError } from '@/core/lib/apiClient';
import {
  legalFloorService,
  LEGAL_FLOOR_CATEGORIES,
  LEGAL_FLOOR_DETAIL_MAX as DETAIL_MAX,
  type LegalFloorCategoryId,
} from '@/core/services/legalFloorService';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 76ch;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};

  /* Focused programmatically after a successful send (see the receipt branch).
     The outline would otherwise draw around a heading nobody clicked. */
  &:focus {
    outline: none;
  }
`;

const Intro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Group = styled.fieldset`
  /* A real fieldset/legend, so the three choices are announced as one named
     group without an aria-labelledby id dance. The reset keeps the browser's
     default chrome out of the card. Deliberately NOT display:flex: a <legend>
     inside a flex container is laid out inconsistently across browsers, so the
     options get their own flex wrapper below instead. */
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
`;

const GroupLabel = styled.legend`
  padding: 0;
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background-color: ${({ theme }) =>
    theme.cardsGlass ? theme.color.glassBg : theme.color.surface};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  transition: background-color 120ms ease;

  &:has(input:checked) {
    background-color: ${({ theme }) =>
      `color-mix(in srgb, ${theme.color.accentCyan} 14%, ${
        theme.cardsGlass ? theme.color.glassBg : theme.color.surface
      })`};
  }
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
`;

const RadioInput = styled.input`
  accent-color: ${({ theme }) => theme.color.accentCyan};
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const RadioText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RadioHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* Errors carry a word, never a colour alone: the danger hue is decoration on
   top of text that says what is wrong. */
const FieldError = styled.p`
  margin: -8px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

/* Same box as every other callout on the page (fill + blur paired, opaque when
   glass is off) — only the layout differs, because this one carries an icon. */
const FormError = styled(Callout)`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

/** A note attached to a field. Given an id so it can be its field's description. */
const FieldHint = styled.p`
  margin: -8px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.textMuted};
`;

/* The box stops dead at the limit. Without this it stops silently, and the
   reporter cannot tell whether the rest of what they typed was sent. */
const Counter = styled.p`
  margin: -8px 0 0;
  align-self: flex-end;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textFaint};
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Received = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Note = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

/** Matches the server's `contact` column (max_length=200), which truncates silently. */
const CONTACT_MAX = 200;

const CATEGORY_ERROR = 'Choose which of the three this is, so it reaches the right person.';
const DETAIL_ERROR = 'Tell us what you found and where. We cannot act on a blank report.';
const SEND_ERROR =
  'We could not send that report. Please try again, and if it keeps failing, that is our fault, not yours.';

export function LegalFloorReportPage() {
  usePageTitle('Report illegal content / TruXSocial');

  const [category, setCategory] = useState<LegalFloorCategoryId | null>(null);
  const [detail, setDetail] = useState('');
  const [targetId, setTargetId] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // The receipt replaces the whole form, so keyboard and screen-reader users
  // must be moved to it rather than left on a button that no longer exists.
  const receiptHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (sent) receiptHeading.current?.focus();
  }, [sent]);

  // A bounce that only paints red text leaves a keyboard or screen-reader user
  // parked on the Send button with no idea which control it was talking about.
  const firstRadio = useRef<HTMLInputElement>(null);
  const detailField = useRef<HTMLTextAreaElement>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    // Validated on submit, not by greying the button out: a control that is
    // inert with no stated reason is the dead-control pattern this repo keeps
    // removing, and it is a cruel one to meet on this particular page.
    const missingCategory = category === null;
    const missingDetail = detail.trim().length === 0;
    setCategoryError(missingCategory ? CATEGORY_ERROR : null);
    setDetailError(missingDetail ? DETAIL_ERROR : null);
    setFormError(null);
    if (missingCategory || missingDetail || category === null) {
      if (missingCategory) firstRadio.current?.focus();
      else if (missingDetail) detailField.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await legalFloorService.report({ category, detail, targetId, contact });
      setSent(true);
    } catch (err) {
      // Discriminate rather than flattening every failure into one line: a
      // rejected field is the reporter's to fix, a dropped connection is not.
      //
      // But a field error is set ONLY for a field the server actually named.
      // The client already guarantees a category is selected, so treating an
      // unexplained rejection as a category problem would park "choose which of
      // the three this is" under a visibly-selected radio, with no way to clear
      // it — and would swallow the one thing the reporter has to be told, which
      // is that the report did not go.
      const rejected =
        err instanceof ApiError && err.code === 'validation' ? (err.fields ?? {}) : {};
      const rejectedDetail = Boolean(rejected.detail);
      const rejectedCategory = Boolean(rejected.category);
      if (rejectedDetail) setDetailError(DETAIL_ERROR);
      if (rejectedCategory) setCategoryError(CATEGORY_ERROR);

      if (!rejectedDetail && !rejectedCategory) setFormError(SEND_ERROR);
      else if (rejectedCategory) firstRadio.current?.focus();
      else detailField.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <Page>
        <Title ref={receiptHeading} tabIndex={-1}>
          Report received
        </Title>
        {/* First, and in the same red the form gives it. This is the screen where
            someone is most likely to stop reading and assume it is handled, so
            the emergency line must not be the third muted paragraph down. */}
        <Callout $tone="urgent">
          If someone is in immediate danger, contact your local emergency services. We are not an
          emergency service and cannot act at that speed.
        </Callout>
        <Received>
          <Note>
            A person will read this. It does not go to a vote, and it is not judged by the Council.
            This route exists because there is nothing here to weigh. We cannot tell you how long
            that will take.
          </Note>
          <Note>
            We deliberately cannot show you a status page for it, and there is no reference number.
            This route is open to anyone, and a way to look a report up would let anyone check
            whether one exists and what happened to it.
          </Note>
          <Note>
            <Link to="/policies/moderation">How moderation decisions get made</Link> explains where
            this route sits relative to the Council.
          </Note>
        </Received>
      </Page>
    );
  }

  return (
    <Page>
      <Title>Report illegal content</Title>
      <Intro>
        This route is for the small, fixed set of material that is unlawful to host. It is separate
        from the five things the Council judges, it is never put to a vote, and one report is
        enough. The rules about reports building up exist to stop mass-reporting silencing people,
        and they have no place here.
      </Intro>
      <Intro>
        <strong>You do not need an account.</strong> You do not need to be the person affected.
      </Intro>
      <Callout $tone="urgent">
        If someone is in immediate danger, contact your local emergency services first. We cannot
        act at that speed.
      </Callout>

      <Card as="form" onSubmit={handleSubmit} noValidate>
        {/* role="radiogroup" rather than the bare fieldset's implicit `group`:
            aria-invalid and aria-required are supported on a radiogroup and are
            ignored on a group, so without it the box is announced as valid while
            the error is painted underneath. The legend still names it, via an
            explicit aria-labelledby that survives the role override. */}
        <Group
          role="radiogroup"
          aria-labelledby="lf-category-label"
          aria-required="true"
          aria-invalid={categoryError !== null}
          aria-describedby={categoryError ? 'lf-category-error' : undefined}
        >
          <GroupLabel id="lf-category-label">What are you reporting?</GroupLabel>
          <Options>
            {LEGAL_FLOOR_CATEGORIES.map((c, i) => (
              <RadioLabel key={c.id}>
                <RadioInput
                  ref={i === 0 ? firstRadio : undefined}
                  type="radio"
                  name="legal-floor-category"
                  value={c.id}
                  checked={category === c.id}
                  onChange={() => {
                    setCategory(c.id);
                    setCategoryError(null);
                  }}
                />
                <RadioText>
                  {c.label}
                  <RadioHint>{c.hint}</RadioHint>
                </RadioText>
              </RadioLabel>
            ))}
          </Options>
        </Group>
        {categoryError && (
          <FieldError id="lf-category-error" role="alert">
            {categoryError}
          </FieldError>
        )}

        <Textarea
          ref={detailField}
          label="What and where?"
          placeholder="Describe what you found and where on TruXSocial it is. Paste any links here."
          value={detail}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setDetail(e.currentTarget.value);
            if (detailError) setDetailError(null);
          }}
          rows={5}
          required
          maxLength={DETAIL_MAX}
          aria-invalid={detailError !== null}
          aria-describedby={detailError ? 'lf-detail-error' : undefined}
        />
        <Counter>
          {detail.length} / {DETAIL_MAX}
        </Counter>
        {detailError && (
          <FieldError id="lf-detail-error" role="alert">
            {detailError}
          </FieldError>
        )}

        {/* Relabelled from "Link or post id". The server matches this against a
            post id exactly and stores the raw value nowhere, so a pasted URL used
            to vanish while the reporter believed they had told us where. The
            description box above IS stored, so links belong there. */}
        <TextField
          label="Post id (optional)"
          placeholder="If you have one"
          aria-describedby="lf-target-hint"
          value={targetId}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTargetId(e.currentTarget.value)}
        />
        <FieldHint id="lf-target-hint">
          Only an exact post id is matched here. If you have a link, paste it into the description
          above instead. That is the part we keep.
        </FieldHint>

        {/* autoComplete off on purpose: some people reporting this are frightened
            of being identified, and a browser helpfully filling in a saved address
            works against the field we have just called optional. */}
        <TextField
          label="How to reach you (optional)"
          placeholder="Email or phone, only if you want to be contacted"
          autoComplete="off"
          maxLength={CONTACT_MAX}
          aria-describedby="lf-contact-hint"
          value={contact}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setContact(e.currentTarget.value)}
        />
        <FieldHint id="lf-contact-hint">
          Optional on purpose. Some people reporting this cannot safely be contacted, and a report
          without a way back is still acted on.
        </FieldHint>

        {formError && (
          <FormError $tone="danger" role="alert" data-testid="legal-floor-error">
            <Icon name="shield" size={16} title="Notice" />
            {formError}
          </FormError>
        )}

        <Row>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send report'}
          </Button>
        </Row>
      </Card>
    </Page>
  );
}
